/**
 * Text box measurement for the print-template editor.
 *
 * Text elements render with `overflow: hidden`, so a box smaller than its text
 * silently swallows glyphs. Everything that sizes a text box measures here, off
 * one hidden probe styled exactly like the real element.
 */
import { ELEMENT_TYPES } from '../schema/documentSchema';
import { resolveFontStack } from '../panels/propertyControls';
import { isTextOnPath } from './textPathLayout';

export function isTextLike(element) {
    return element?.type === ELEMENT_TYPES.TEXT || element?.type === ELEMENT_TYPES.PLACEHOLDER;
}

function textOf(element) {
    if (element?.content) return element.content;
    if (element?.type === ELEMENT_TYPES.PLACEHOLDER) {
        return `{{${element.placeholderKey || 'field'}}}`;
    }
    // A blank box still occupies one line — measure a space, not nothing.
    return ' ';
}

let probe = null;
function getProbe() {
    if (typeof document === 'undefined') return null;
    if (probe && probe.isConnected) return probe;
    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:absolute;left:-99999px;top:0;visibility:hidden;'
        + 'pointer-events:none;margin:0;padding:0;border:0;';
    document.body.appendChild(probe);
    return probe;
}

/** Style the probe like the element's text, minus any box constraint. */
function styleProbe(p, element, styles, whiteSpace) {
    const s = p.style;
    s.fontFamily = resolveFontStack(styles);
    s.fontSize = `${Number(styles.fontSize) || 16}px`;
    s.fontWeight = styles.fontWeight || 400;
    s.fontStyle = styles.fontStyle || 'normal';
    s.lineHeight = String(styles.lineHeight ?? 1.3);
    s.letterSpacing = styles.letterSpacing != null ? `${styles.letterSpacing}px` : 'normal';
    s.textTransform = styles.textTransform || 'none';
    s.textDecoration = 'none';
    s.whiteSpace = whiteSpace;
    s.wordBreak = 'normal';
    s.width = 'auto';
    p.textContent = textOf(element);
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Padding + border the text sits inside, in element-box pixels. The stroke is
 *  painted on the glyph outline, so it takes no room from the content. */
function boxInsets(styles = {}) {
    const pad = (side) => num(styles[`padding${side}`] ?? styles.padding);
    const bw = num(styles.borderWidth);
    return {
        x: pad('Left') + pad('Right') + bw * 2,
        y: pad('Top') + pad('Bottom') + bw * 2,
    };
}

/**
 * Natural size of the text with NO wrapping (`white-space: pre`, so explicit
 * newlines count but nothing else breaks). Independent of the element's own box,
 * so feeding the result back into that box can never oscillate. Used by autosize.
 */
export function measureNaturalText(element, styles = {}) {
    const p = getProbe();
    if (!p) return null;
    styleProbe(p, element, styles, 'pre');
    const r = p.getBoundingClientRect();
    return { w: r.width, h: r.height };
}

/**
 * Smallest box that shows the text in full, given the width the caller wants.
 *
 * - width  = the text's `min-content` width. For a non-wrapping element that is
 *   the whole longest line; for a wrapping one it is the widest word, so a
 *   narrow wrapping column is still allowed — just not one that cuts a word.
 * - height = what the text needs at `requestedWidth` (or at the min width, if
 *   the request was narrower), wrapping exactly as the element will.
 *
 * Both include padding and border, i.e. they are element-box numbers.
 */
export function minTextBox(element, styles = {}, requestedWidth) {
    const p = getProbe();
    if (!p) return null;
    const ws = styles.whiteSpace || 'pre-wrap';
    styleProbe(p, element, styles, ws);

    p.style.width = 'min-content';
    const minContentW = p.getBoundingClientRect().width;

    const insets = boxInsets(styles);
    const askedContentW = Math.max(0, num(requestedWidth) - insets.x);
    p.style.width = `${Math.max(minContentW, askedContentW)}px`;
    const neededH = p.getBoundingClientRect().height;
    p.style.width = 'auto';

    return {
        width: Math.max(1, Math.ceil(minContentW + insets.x)),
        height: Math.max(1, Math.ceil(neededH + insets.y)),
    };
}

/**
 * Grow a width/height patch until the text fits. Only ever enlarges — a box the
 * user made bigger than its text stays that way.
 */
export function clampTextSize(element) {
    if (!isTextLike(element)) return element;
    // Text on a path doesn't wrap or clip inside its box — the box is the curve's
    // frame, so growing it to "fit" would silently reshape the path.
    if (isTextOnPath(element)) return element;
    const min = minTextBox(element, element.styles || {}, element.width);
    if (!min) return element;
    const width = Math.max(num(element.width), min.width);
    const height = Math.max(num(element.height), min.height);
    if (width === element.width && height === element.height) return element;
    return { ...element, width, height };
}
